import type { SolvedRhombus } from './solver.model';

/**
 * Solve a rhombus given side length and included angle.
 * Returns all properties and vertices in canonical placement.
 *
 * A rhombus is a parallelogram with all sides equal (b = s).
 * Canonical placement: A at origin, base along +x.
 * A = (0,0), B = (s,0), C = (s + s*cos(th), s*sin(th)), D = (s*cos(th), s*sin(th))
 */
export function solveRhombus(s: number, angleDeg = 60): SolvedRhombus {
  if (s <= 0) {
    throw new Error(`Side length must be positive: s=${s}`);
  }
  if (angleDeg <= 0 || angleDeg >= 180) {
    throw new Error(`Angle must be between 0° and 180°: angle=${angleDeg}`);
  }

  const angleRad = (angleDeg * Math.PI) / 180;
  const h = s * Math.sin(angleRad);
  const offset = s * Math.cos(angleRad);

  const A: [number, number] = [0, 0];
  const B: [number, number] = [s, 0];
  const C: [number, number] = [s + offset, h];
  const D: [number, number] = [offset, h];

  const d1 = Math.sqrt((C[0] - A[0]) ** 2 + (C[1] - A[1]) ** 2);
  const d2 = Math.sqrt((D[0] - B[0]) ** 2 + (D[1] - B[1]) ** 2);

  return {
    vertices: { A, B, C, D },
    s,
    angle: angleDeg,
    h,
    d1,
    d2,
    area: s * h,
    perimeter: 4 * s,
  };
}
